import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Copy, Download, Check, Upload, ArrowRight } from 'lucide-react';
import { Checkbox } from '../tailgrids/core/checkbox.js';
import { Button, Modal, Select, Spinner, Textarea } from '../ui/index.js';
import { workflowInstructionApi } from '../../services/api/workflow-instruction.api.js';
import type { WorkflowBuilderStep } from '../../pages/admin/WorkflowBuilderTypes.js';

interface ExportInstructionsModalProps {
  open: boolean;
  onClose: () => void;
  workflowId?: string;
  steps: WorkflowBuilderStep[];
  selectedStepId?: string | null;
  onImport?: (content: string, fileName: string) => void;
}

type Scope = 'full' | 'selected' | 'blank';

export const ExportInstructionsModal: React.FC<ExportInstructionsModalProps> = ({
  open,
  onClose,
  workflowId,
  steps,
  selectedStepId,
  onImport,
}) => {
  const [scope, setScope] = useState<Scope>(workflowId ? 'full' : 'blank');
  const [selectedStepIds, setSelectedStepIds] = useState<Set<string>>(
    selectedStepId ? new Set([selectedStepId]) : new Set(),
  );
  const [includeRoles, setIncludeRoles] = useState(true);
  const [includeOutputSchemas, setIncludeOutputSchemas] = useState(true);
  const [preview, setPreview] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [fileName, setFileName] = useState('workflow-instructions.md');
  const [importText, setImportText] = useState('');
  const [importDragOver, setImportDragOver] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!mountedRef.current) return;
      setLoading(true);
      try {
        const options = {
          workflowId: scope !== 'blank' ? workflowId : undefined,
          stepIds: scope === 'selected' ? Array.from(selectedStepIds) : undefined,
          scope,
          includeRoles,
          includeOutputSchemas,
        };
        const result = workflowId
          ? await workflowInstructionApi.exportForWorkflow(workflowId, options)
          : await workflowInstructionApi.exportInstructions(options);
        if (mountedRef.current) {
          setPreview(result.content);
          setFileName(result.fileName);
        }
      } catch (e) {
        if (mountedRef.current) {
          setPreview(`Error generating instructions: ${(e as Error).message}`);
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [open, scope, selectedStepIds, includeRoles, includeOutputSchemas, workflowId]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(preview);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers without clipboard API
      const textarea = document.createElement('textarea');
      textarea.value = preview;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([preview], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  const toggleStep = (stepId: string) => {
    setSelectedStepIds((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) next.delete(stepId);
      else next.add(stepId);
      return next;
    });
  };

  const handleImportDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setImportDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          onImport?.(reader.result, file.name);
        }
      };
      reader.readAsText(file);
    },
    [onImport],
  );

  const browseForFile = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.txt,.md,.json,.yaml,.yml';
    input.onchange = () => {
      const file = input.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') onImport?.(reader.result, file.name);
      };
      reader.readAsText(file);
    };
    input.click();
  }, [onImport]);

  const handleImportContinue = useCallback(() => {
    if (!importText.trim()) return;
    onImport?.(importText.trim(), 'pasted-result.md');
    setImportText('');
  }, [importText, onImport]);

  const scopeOptions = [
    { value: 'full', label: 'Full workflow', disabled: !workflowId },
    { value: 'selected', label: 'Selected steps', disabled: !workflowId || steps.length === 0 },
    { value: 'blank', label: 'Blank template', disabled: false },
  ] as const;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="AI collaborate"
      description="Export instructions to an external AI model, then send the result to the workflow assistant"
      maxWidth="2xl"
    >
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        {/* Options */}
        <div className="w-full shrink-0 space-y-4 overflow-y-auto border-b p-4 md:w-60 md:border-r md:border-b-0">
          <div>
            <p className="mb-2 text-xs font-medium text-muted">Scope</p>
            <Select aria-label="Scope" value={scope} onChange={(e) => setScope(e.target.value as Scope)}>
              {scopeOptions.map((opt) => (
                <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>

          {scope === 'selected' && steps.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-medium text-muted">Steps to include</p>
              <div className="max-h-40 space-y-1 overflow-y-auto">
                {steps.map((step) => (
                  <Checkbox
                    key={step.id}
                    isSelected={selectedStepIds.has(step.id.toString())}
                    onChange={() => toggleStep(step.id.toString())}
                    className="text-xs text-body"
                  >
                    {step.name || 'Unnamed'}
                  </Checkbox>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="mb-2 text-xs font-medium text-muted">Include</p>
            <div className="space-y-1.5">
              <Checkbox isSelected={includeRoles} onChange={setIncludeRoles} className="text-sm text-body">
                Agent roles
              </Checkbox>
              <Checkbox
                isSelected={includeOutputSchemas}
                onChange={setIncludeOutputSchemas}
                className="text-sm text-body"
              >
                Output channel schemas
              </Checkbox>
            </div>
          </div>

          <div className="space-y-3 border-t pt-4">
            <p className="text-xs font-medium text-heading">What's next</p>
            <ol className="list-inside list-decimal space-y-1 text-xs text-muted">
              <li>Copy or download the instructions</li>
              <li>Paste into ChatGPT, Claude, or any AI</li>
              <li>Send the result to the workflow assistant</li>
            </ol>

            <div
              onDragOver={(e) => {
                e.preventDefault();
                setImportDragOver(true);
              }}
              onDragLeave={() => setImportDragOver(false)}
              onDrop={handleImportDrop}
              className={`flex flex-col items-center gap-1.5 rounded-lg border-2 border-dashed p-3 transition-colors ${
                importDragOver ? 'border-accent bg-accent/5' : 'border-strong'
              }`}
            >
              <Upload size={16} className={importDragOver ? 'text-accent-text' : 'text-faint'} />
              <span className="text-center text-xs text-muted">Drop a file here or</span>
              <Button variant="secondary" size="sm" onClick={browseForFile}>
                Browse files
              </Button>
            </div>

            <div className="space-y-1.5">
              <p className="text-[11px] text-muted">Or paste the AI's response</p>
              <Textarea
                aria-label="AI response"
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                placeholder="Paste result text here..."
                rows={4}
              />
              <Button size="sm" className="w-full" onClick={handleImportContinue} disabled={!importText.trim()}>
                Send to assistant <ArrowRight size={12} />
              </Button>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex shrink-0 items-center justify-between gap-2 border-b px-4 py-2">
            <span className="truncate text-xs text-muted">{loading ? 'Generating...' : `Preview: ${fileName}`}</span>
            <div className="flex items-center gap-1">
              <Button variant="secondary" size="sm" onClick={handleCopy} disabled={loading || !preview}>
                {copied ? (
                  <>
                    <Check size={12} /> Copied
                  </>
                ) : (
                  <>
                    <Copy size={12} /> Copy
                  </>
                )}
              </Button>
              <Button variant="secondary" size="sm" onClick={handleDownload} disabled={loading || !preview}>
                <Download size={12} /> Download
              </Button>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-auto p-4">
            {loading ? (
              <div className="flex h-32 items-center justify-center gap-2 text-muted">
                <Spinner /> Generating instructions...
              </div>
            ) : (
              <pre className="whitespace-pre-wrap font-mono text-xs leading-relaxed text-body">{preview}</pre>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
};
